import { createFileRoute } from '@tanstack/react-router';
import { Download, Inbox, Plus, Search } from 'lucide-react';
import { useState } from 'react';

import {
    AdminBadge,
    AdminStatusDot,
} from '@apps/web/features/admin_design/admin_badge';
import {
    AdminButton,
    AdminInput,
    AdminSearchInput,
    AdminSelect,
    AdminToggle,
} from '@apps/web/features/admin_design/admin_controls';
import {
    AdminCard,
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
    AdminSection,
    AdminStat,
} from '@apps/web/features/admin_design/admin_shell';
import {
    AdminSkeleton,
    AdminTableSkeleton,
} from '@apps/web/features/admin_design/admin_skeleton';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
} from '@apps/web/features/admin_design/admin_table';
import { AdminTabs } from '@apps/web/features/admin_design/admin_tabs';
import { cn } from '@apps/web/lib/utils';
import {
    BORDER_SWATCHES,
    GROUND_SWATCHES,
    INK_SWATCHES,
    MOTION_ROWS,
    PhoebePurpleStory,
    PURPLE_SWATCHES,
    RADIUS_ROWS,
    staggerProps,
    STATUS_SWATCHES,
    SwatchColumn,
    TYPE_ROWS,
} from '@apps/web/routes/_app/admin/design_kit_foundations';

// Re-export so downstream tests that pin the stagger contract keep a
// stable import path even though the helper lives in the extracted
// foundations module.
export { staggerProps } from '@apps/web/routes/_app/admin/design_kit_foundations';

/*
 * Living showcase of the admin design kit (PHO-14461). Not linked
 * from the sidebar — visit /admin/design-kit directly. Parts B–D use
 * this page as the conformance reference when migrating pages onto
 * the kit.
 *
 * PHO-14461 Part C: Phoebe purple (#5533ee) is the signature. The
 * Foundations tab opens with a dedicated "Phoebe purple story"
 * section that lays the full purple scale on the paper canvas so a
 * reviewer can judge legibility, contrast, and the interaction of
 * every primitive against the brand hue in one glance.
 */

export const Route = createFileRoute('/_app/admin/design-kit')({
    head: () => ({
        meta: [
            {
                title: 'Design Kit | Phoebe Admin',
            },
        ],
    }),
    component: AdminDesignKitPage,
});

const SAMPLE_ROWS = [
    {
        id: '1',
        name: 'Sunrise Home Care',
        region: 'Austin, TX',
        caregivers: 128,
        status: 'ok' as const,
        statusLabel: 'Healthy',
    },
    {
        id: '2',
        name: 'Bluebonnet Care Group',
        region: 'Dallas, TX',
        caregivers: 42,
        status: 'warn' as const,
        statusLabel: 'Attention',
    },
    {
        id: '3',
        name: 'Hill Country Caregiving',
        region: 'San Antonio, TX',
        caregivers: 7,
        status: 'danger' as const,
        statusLabel: 'Failing',
    },
    {
        id: '4',
        name: 'Coastal Bend Health',
        region: 'Corpus Christi, TX',
        caregivers: 61,
        status: 'ok' as const,
        statusLabel: 'Healthy',
    },
];

function AdminDesignKitPage() {
    const [tab, setTab] = useState('foundations');
    const [toggleOn, setToggleOn] = useState(true);
    const [density, setDensity] = useState<'regular' | 'compact'>('regular');
    const [search, setSearch] = useState('');

    return (
        <AdminPage>
            <AdminPageHeader
                title="Design Kit"
                meta="admin_design"
                actions={
                    <AdminButton variant="primary" size="md">
                        <Plus />
                        Primary action
                    </AdminButton>
                }
            >
                <AdminTabs
                    items={[
                        { value: 'foundations', label: 'Foundations' },
                        { value: 'primitives', label: 'Primitives' },
                        { value: 'data', label: 'Data' },
                        { value: 'states', label: 'States' },
                    ]}
                    value={tab}
                    onValueChange={setTab}
                />
            </AdminPageHeader>
            <AdminPageBody>
                {tab === 'foundations' ? (
                    <div className="flex max-w-5xl flex-col gap-10">
                        <AdminSection
                            title="Phoebe purple story"
                            description="The signature scale — soft, wash, border, base, strong — laid on the paper canvas so the interaction between brand hue and surface reads at a glance."
                        >
                            <PhoebePurpleStory />
                        </AdminSection>
                        <AdminSection
                            title="Color"
                            description="Off-white paper, ink with a slight purple cast, one signature hue. Every swatch prints its CSS variable and hex."
                        >
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                <SwatchColumn
                                    title="Phoebe purple"
                                    swatches={PURPLE_SWATCHES}
                                />
                                <SwatchColumn
                                    title="Ground"
                                    swatches={GROUND_SWATCHES}
                                />
                                <SwatchColumn
                                    title="Ink"
                                    swatches={INK_SWATCHES}
                                />
                                <SwatchColumn
                                    title="Border"
                                    swatches={BORDER_SWATCHES}
                                />
                                <SwatchColumn
                                    title="Status text"
                                    swatches={STATUS_SWATCHES}
                                />
                            </div>
                        </AdminSection>
                        <AdminSection
                            title="Typography"
                            description="13px body default. Micro-caps for section labels, table headers, and stat labels."
                        >
                            <AdminCard>
                                {TYPE_ROWS.map((row, index) => {
                                    const stagger = staggerProps(index);
                                    return (
                                        <div
                                            key={row.variable}
                                            data-testid={`type-row-${index}`}
                                            className={cn(
                                                'border-admin-border flex flex-col gap-1 border-b py-3 last:border-b-0 md:flex-row md:items-baseline md:gap-6',
                                                stagger.className,
                                            )}
                                            style={stagger.style}
                                        >
                                            <div className="md:w-64 md:shrink-0">
                                                <div className="text-admin-sm text-admin-ink-muted">
                                                    {row.label}
                                                </div>
                                                <code className="text-admin-xs text-admin-ink-faint admin-tabular font-mono">
                                                    {row.variable}
                                                </code>
                                            </div>
                                            <div className={row.className}>
                                                {row.sample}
                                            </div>
                                        </div>
                                    );
                                })}
                            </AdminCard>
                        </AdminSection>
                        <div className="grid gap-4 md:grid-cols-2">
                            <AdminSection
                                title="Radii"
                                description="4 / 6 / 8. Nothing else."
                            >
                                <AdminCard>
                                    {RADIUS_ROWS.map((row) => (
                                        <div
                                            key={row.variable}
                                            className="border-admin-border flex items-center gap-4 border-b py-3 last:border-b-0"
                                        >
                                            <div
                                                aria-hidden="true"
                                                className={cn(
                                                    'border-admin-border-strong bg-admin-sunken size-10 shrink-0 border',
                                                    row.className,
                                                )}
                                            />
                                            <div className="flex-1">
                                                <div className="text-admin-base text-admin-ink font-medium">
                                                    {row.label}
                                                </div>
                                                <code className="text-admin-xs text-admin-ink-muted admin-tabular font-mono">
                                                    {row.variable}
                                                </code>
                                            </div>
                                            <code className="text-admin-xs text-admin-ink-secondary admin-tabular font-mono">
                                                {row.value}
                                            </code>
                                        </div>
                                    ))}
                                </AdminCard>
                            </AdminSection>
                            <AdminSection
                                title="Motion"
                                description="cubic-bezier(0.16,1,0.3,1) enters. No 300ms defaults."
                            >
                                <AdminCard>
                                    {MOTION_ROWS.map((row, index) => {
                                        const stagger = staggerProps(index);
                                        return (
                                            <div
                                                key={row.variable}
                                                data-testid={`motion-row-${index}`}
                                                className={cn(
                                                    'border-admin-border flex items-baseline gap-4 border-b py-2 last:border-b-0',
                                                    stagger.className,
                                                )}
                                                style={stagger.style}
                                            >
                                                <div className="text-admin-base text-admin-ink w-24 shrink-0 font-medium">
                                                    {row.label}
                                                </div>
                                                <code className="text-admin-xs text-admin-ink-muted admin-tabular flex-1 font-mono">
                                                    {row.variable}
                                                </code>
                                                <code className="text-admin-xs text-admin-ink-secondary admin-tabular font-mono">
                                                    {row.value}
                                                </code>
                                            </div>
                                        );
                                    })}
                                </AdminCard>
                            </AdminSection>
                        </div>
                    </div>
                ) : tab === 'primitives' ? (
                    <div className="flex max-w-4xl flex-col gap-10">
                        <AdminSection
                            title="Buttons"
                            description="Primary fills with Phoebe purple. Secondary hover lifts the hairline toward purple. Ghost hover paints an ultra-light purple wash. Danger stays outline until hover."
                        >
                            <AdminCard>
                                <div className="flex flex-wrap items-center gap-2 pb-3">
                                    <AdminButton variant="primary">
                                        Primary
                                    </AdminButton>
                                    <AdminButton variant="secondary">
                                        Secondary
                                    </AdminButton>
                                    <AdminButton variant="ghost">
                                        Ghost
                                    </AdminButton>
                                    <AdminButton variant="danger">
                                        Danger
                                    </AdminButton>
                                    <AdminButton
                                        variant="secondary"
                                        disabled={true}
                                    >
                                        Disabled
                                    </AdminButton>
                                </div>
                                <div className="border-admin-border flex flex-wrap items-center gap-2 border-t pt-3">
                                    <AdminButton variant="secondary" size="sm">
                                        <Download />
                                        Small
                                    </AdminButton>
                                    <AdminButton variant="secondary" size="md">
                                        <Download />
                                        Medium
                                    </AdminButton>
                                    <AdminButton
                                        variant="secondary"
                                        size="icon"
                                        aria-label="Search"
                                    >
                                        <Search />
                                    </AdminButton>
                                    <AdminButton
                                        variant="secondary"
                                        size="iconSm"
                                        aria-label="Search small"
                                    >
                                        <Search />
                                    </AdminButton>
                                </div>
                                <div className="border-admin-border flex flex-wrap items-center gap-8 border-t pt-3">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-admin-xs text-admin-ink-muted">
                                            Isolated · hitArea
                                        </span>
                                        <AdminButton
                                            variant="secondary"
                                            size="iconSm"
                                            hitArea={true}
                                            aria-label="Isolated search"
                                        >
                                            <Search />
                                        </AdminButton>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <span className="text-admin-xs text-admin-ink-muted">
                                            Toolbar · default (no overlap)
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <AdminButton
                                                variant="secondary"
                                                size="iconSm"
                                                aria-label="Toolbar search"
                                            >
                                                <Search />
                                            </AdminButton>
                                            <AdminButton
                                                variant="secondary"
                                                size="iconSm"
                                                aria-label="Toolbar download"
                                            >
                                                <Download />
                                            </AdminButton>
                                        </div>
                                    </div>
                                </div>
                            </AdminCard>
                        </AdminSection>
                        <AdminSection
                            title="Inputs"
                            description="32px controls, hairline borders, Phoebe purple focus border + ring."
                        >
                            <AdminCard>
                                <div className="flex flex-col gap-2.5">
                                    <AdminSearchInput
                                        placeholder="Search organizations…"
                                        value={search}
                                        onChange={(event) =>
                                            setSearch(event.target.value)
                                        }
                                        onClear={() => setSearch('')}
                                    />
                                    <AdminInput placeholder="Internal name" />
                                    <div className="flex items-center gap-3">
                                        <AdminSelect
                                            value={density}
                                            onValueChange={(value) =>
                                                setDensity(
                                                    value as typeof density,
                                                )
                                            }
                                            aria-label="Table density"
                                            options={[
                                                {
                                                    value: 'regular',
                                                    label: 'Regular density',
                                                },
                                                {
                                                    value: 'compact',
                                                    label: 'Compact density',
                                                },
                                            ]}
                                        />
                                        <AdminToggle
                                            aria-label="Example toggle"
                                            checked={toggleOn}
                                            onCheckedChange={setToggleOn}
                                        />
                                        <span className="text-admin-sm text-admin-ink-muted">
                                            Toggle · Phoebe purple track when on
                                        </span>
                                    </div>
                                </div>
                            </AdminCard>
                        </AdminSection>
                        <AdminSection
                            title="Badges"
                            description="Outline is the default. Solid is reserved for primary/severe callouts."
                        >
                            <AdminCard>
                                <div className="flex flex-col gap-3">
                                    <div>
                                        <div className="text-admin-xs text-admin-ink-muted mb-2 font-medium tracking-[0.06em] uppercase">
                                            Outline
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <AdminBadge tone="neutral">
                                                Neutral
                                            </AdminBadge>
                                            <AdminBadge tone="ok">
                                                Approved
                                            </AdminBadge>
                                            <AdminBadge tone="warn">
                                                Pending
                                            </AdminBadge>
                                            <AdminBadge tone="danger">
                                                Rejected
                                            </AdminBadge>
                                            <AdminBadge tone="info">
                                                Syncing
                                            </AdminBadge>
                                            <AdminBadge tone="outline">
                                                Sandbox
                                            </AdminBadge>
                                        </div>
                                    </div>
                                    <div className="border-admin-border border-t pt-3">
                                        <div className="text-admin-xs text-admin-ink-muted mb-2 font-medium tracking-[0.06em] uppercase">
                                            Solid
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <AdminBadge
                                                tone="ink-primary"
                                                variant="solid"
                                            >
                                                Primary
                                            </AdminBadge>
                                            <AdminBadge
                                                tone="danger"
                                                variant="solid"
                                            >
                                                Critical
                                            </AdminBadge>
                                        </div>
                                    </div>
                                    <div className="border-admin-border border-t pt-3">
                                        <div className="text-admin-xs text-admin-ink-muted mb-2 font-medium tracking-[0.06em] uppercase">
                                            Status dots
                                        </div>
                                        <div className="flex flex-wrap items-center gap-4">
                                            <AdminStatusDot
                                                tone="ok"
                                                label="Healthy"
                                            />
                                            <AdminStatusDot
                                                tone="warn"
                                                label="Degraded"
                                            />
                                            <AdminStatusDot
                                                tone="danger"
                                                label="Down"
                                            />
                                            <AdminStatusDot
                                                tone="purple"
                                                label="Live"
                                            />
                                            <AdminStatusDot
                                                tone="neutral"
                                                label="Idle"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </AdminCard>
                        </AdminSection>
                        <AdminSection
                            title="Cards"
                            description="Hairline border, no shadow at rest. Header divider disappears with flush={true}."
                        >
                            <div className="grid gap-4 md:grid-cols-2">
                                <AdminCard
                                    title="Voice settings"
                                    description="Applies to live mode."
                                    actions={
                                        <AdminButton variant="ghost" size="sm">
                                            Edit
                                        </AdminButton>
                                    }
                                >
                                    <p className="text-admin-base text-admin-ink-secondary">
                                        Card content sits on the white surface
                                        with 13px body text.
                                    </p>
                                </AdminCard>
                                <AdminCard>
                                    <p className="text-admin-base text-admin-ink-secondary">
                                        Untitled card — no header, just the
                                        surface + hairline.
                                    </p>
                                </AdminCard>
                            </div>
                        </AdminSection>
                    </div>
                ) : tab === 'data' ? (
                    <div className="flex flex-col gap-10">
                        <AdminSection title="Stats">
                            <div className="grid gap-3 sm:grid-cols-4">
                                <AdminStat label="Errors" value="4" />
                                <AdminStat
                                    label="In progress"
                                    value="1,284"
                                    hint="last 30 days"
                                />
                                <AdminStat
                                    label="Completed"
                                    value="12,842"
                                    hint="+412 vs prior 30d"
                                />
                                <AdminStat
                                    label="Median latency"
                                    value="184ms"
                                />
                            </div>
                        </AdminSection>
                        <AdminSection
                            title="Table"
                            description="Sticky micro-caps header with backdrop blur, hairline row dividers, tabular numerals on numeric columns."
                            actions={
                                <AdminSelect
                                    value={density}
                                    onValueChange={(value) =>
                                        setDensity(value as typeof density)
                                    }
                                    aria-label="Row density"
                                    options={[
                                        {
                                            value: 'regular',
                                            label: 'Regular',
                                        },
                                        {
                                            value: 'compact',
                                            label: 'Compact',
                                        },
                                    ]}
                                />
                            }
                        >
                            <AdminCard flush={true}>
                                <div className="max-h-72 overflow-y-auto">
                                    <AdminTable density={density}>
                                        <AdminTableHeader>
                                            <tr>
                                                <AdminTableHeadCell>
                                                    Organization
                                                </AdminTableHeadCell>
                                                <AdminTableHeadCell>
                                                    Region
                                                </AdminTableHeadCell>
                                                <AdminTableHeadCell
                                                    numeric={true}
                                                >
                                                    Caregivers
                                                </AdminTableHeadCell>
                                                <AdminTableHeadCell>
                                                    Status
                                                </AdminTableHeadCell>
                                            </tr>
                                        </AdminTableHeader>
                                        <AdminTableBody>
                                            {SAMPLE_ROWS.map((row, index) => {
                                                const stagger =
                                                    staggerProps(index);
                                                return (
                                                    <AdminTableRow
                                                        key={row.id}
                                                        data-testid={`sample-row-${index}`}
                                                        selected={index === 1}
                                                        className={
                                                            stagger.className
                                                        }
                                                        style={stagger.style}
                                                    >
                                                        <AdminTableCell className="text-admin-ink font-medium">
                                                            {row.name}
                                                        </AdminTableCell>
                                                        <AdminTableCell>
                                                            {row.region}
                                                        </AdminTableCell>
                                                        <AdminTableCell
                                                            numeric={true}
                                                        >
                                                            {row.caregivers}
                                                        </AdminTableCell>
                                                        <AdminTableCell>
                                                            <AdminStatusDot
                                                                tone={
                                                                    row.status
                                                                }
                                                                label={
                                                                    row.statusLabel
                                                                }
                                                            />
                                                        </AdminTableCell>
                                                    </AdminTableRow>
                                                );
                                            })}
                                        </AdminTableBody>
                                    </AdminTable>
                                </div>
                            </AdminCard>
                        </AdminSection>
                    </div>
                ) : (
                    <div className="flex max-w-4xl flex-col gap-10">
                        <AdminSection
                            title="Empty state"
                            description="One monochrome icon, 14px ink heading, 12px ink-muted subcopy, one secondary CTA."
                        >
                            <AdminCard flush={true}>
                                <AdminEmptyState
                                    icon={Inbox}
                                    title="No caregivers yet"
                                    description="Caregivers appear here once an organization syncs from its EHR."
                                    action={
                                        <AdminButton
                                            variant="secondary"
                                            size="sm"
                                        >
                                            Clear filters
                                        </AdminButton>
                                    }
                                />
                            </AdminCard>
                        </AdminSection>
                        <AdminSection
                            title="Error state"
                            description="Monochrome AlertCircle icon (same tone as empty), retry as a secondary CTA. The glyph carries the semantic, not color."
                        >
                            <AdminCard flush={true}>
                                <AdminErrorState
                                    title="Failed to load caregivers"
                                    description="The request timed out. Retry, or check the API service."
                                    onRetry={() => undefined}
                                />
                            </AdminCard>
                        </AdminSection>
                        <AdminSection
                            title="Skeleton shapes"
                            description="Shape prop routes to the same radii as the primitive it stands in for."
                        >
                            <AdminCard>
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-4">
                                        <span className="text-admin-xs text-admin-ink-muted w-16 font-medium tracking-[0.06em] uppercase">
                                            text
                                        </span>
                                        <div className="flex flex-1 flex-col gap-2">
                                            <AdminSkeleton className="w-3/4" />
                                            <AdminSkeleton className="w-1/2" />
                                        </div>
                                    </div>
                                    <div className="border-admin-border flex items-center gap-4 border-t pt-3">
                                        <span className="text-admin-xs text-admin-ink-muted w-16 font-medium tracking-[0.06em] uppercase">
                                            card
                                        </span>
                                        <AdminSkeleton
                                            shape="card"
                                            className="h-16 w-40"
                                        />
                                    </div>
                                    <div className="border-admin-border flex items-center gap-4 border-t pt-3">
                                        <span className="text-admin-xs text-admin-ink-muted w-16 font-medium tracking-[0.06em] uppercase">
                                            circle
                                        </span>
                                        <AdminSkeleton
                                            shape="circle"
                                            className="size-10"
                                        />
                                    </div>
                                </div>
                            </AdminCard>
                        </AdminSection>
                        <AdminSection
                            title="Table skeleton"
                            description="Loading keeps the final layout's shape — no spinners on data pages."
                        >
                            <AdminCard flush={true}>
                                <AdminTableSkeleton
                                    columns={[
                                        'Organization',
                                        'Region',
                                        'Caregivers',
                                        'Status',
                                    ]}
                                    rows={4}
                                />
                            </AdminCard>
                        </AdminSection>
                    </div>
                )}
            </AdminPageBody>
        </AdminPage>
    );
}
